import { getCachedReport } from "telemetry/capabilitiesReporter.ts";

/**
 * "Download our app" prompt for TV browsers (FireTV via Amazon Silk, Roku).
 * This default config also works offline; the TvAppPromptConfig JSON from
 * remote-config (edited in the admin TV Config page) deep-merges over it.
 * Store URLs live here as the single source of truth (Footer, Shop pages and
 * Help import them from this file).
 */
export const FIRETV_STORE_URL = "https://www.amazon.co.uk/dp/B0FCYQ3QY7";
export const ROKU_STORE_URL = "https://channelstore.roku.com/en-gb/details/f40f6c4e12bfb9b619aa4530404178ea:bf00789f9ba54a68d7fc648d46ddbfc5/game-city-party-games-on-tv";

// Opens the native Appstore client directly on the app's detail page when the
// browser is running on a Fire device — one click from Install instead of the
// remote-hostile amazon.co.uk website. Unknown-scheme navigations elsewhere
// are silent no-ops, which openAppStore's web fallback covers. Roku has no
// browser-reachable Channel Store deep link, so it stays web-only.
export const FIRETV_DEEPLINK_URL = "amzn://apps/android?asin=B0FCYQ3QY7";

export const DEFAULT_TV_APP_PROMPT = {
    // Off by default: the prompt only runs where the TvAppPromptConfig
    // remote-config key explicitly enables it (TV Config in the admin).
    // Keeps environments whose API/config predate the feature prompt-free.
    enabled: false,
    // The full-screen interstitial can be turned off independently of the peek.
    interstitialEnabled: false,
    reShowDays: 7,
    platforms: {
        firetv: { enabled: true, label: "Get the Fire TV app", url: FIRETV_STORE_URL, deepLinkUrl: FIRETV_DEEPLINK_URL },
        roku: { enabled: true, label: "Get the Roku app", url: ROKU_STORE_URL, deepLinkUrl: null },
    },
};

/**
 * Open the platform's store page for the app. Prefers the native deep link
 * (Fire TV Appstore) and falls back to the web store URL if nothing has taken
 * over the screen within ~1.5s — covers browsers that sniff as TV but can't
 * handle the scheme. Must be called from a user gesture.
 */
export const openAppStore = ({ deepLinkUrl, url }) => {
    if (!deepLinkUrl) {
        window.open(url, "_blank", "noopener");
        return;
    }
    // location.href (not window.open) for both attempts: popups carrying a
    // custom scheme are blocked, and the fallback fires outside the original
    // gesture so a popup would be blocked too.
    const fallback = window.setTimeout(() => {
        if (!document.hidden) window.location.href = url;
    }, 1500);
    const cancel = () => window.clearTimeout(fallback);
    document.addEventListener("visibilitychange", cancel, { once: true });
    window.addEventListener("pagehide", cancel, { once: true });
    window.location.href = deepLinkUrl;
};

/**
 * Shared dismissal memory for the prompt surfaces (peek, interstitial — each
 * passes its own storage key). Dismissals expire after reShowDays.
 */
export const isPromptDismissed = (storageKey, reShowDays) => {
    try {
        const stored = Number(localStorage.getItem(storageKey));
        if (!stored) return false;
        return Date.now() - stored < (reShowDays || 0) * 86400000;
    } catch {
        return false;
    }
};

export const markPromptDismissed = (storageKey) => {
    try { localStorage.setItem(storageKey, String(Date.now())); } catch { /* ignore */ }
};

/**
 * Merge the remote-config JSON string over the bundled defaults.
 * Bad/missing JSON falls back to the defaults.
 */
export const mergeTvAppPromptConfig = (remoteJson) => {
    if (!remoteJson) return DEFAULT_TV_APP_PROMPT;
    try {
        const parsed = JSON.parse(remoteJson);
        return {
            ...DEFAULT_TV_APP_PROMPT,
            ...parsed,
            platforms: Object.fromEntries(
                Object.keys(DEFAULT_TV_APP_PROMPT.platforms).map((p) => [
                    p,
                    { ...DEFAULT_TV_APP_PROMPT.platforms[p], ...(parsed.platforms?.[p] || {}) },
                ])
            ),
        };
    } catch (e) {
        console.warn("TvAppPrompt: invalid remote config JSON, using defaults", e);
        return DEFAULT_TV_APP_PROMPT;
    }
};

/**
 * Which app platform does this browser belong to, if any?
 * Prefers the cached capabilities report (built at startup by
 * initCapabilitiesReporting); falls back to a direct UA sniff so the prompt
 * still works on a first-ever visit before the report is cached.
 * Note: Silk alone does NOT set isFireTV in the report (that needs an AFT
 * model code), so both flags are OR'd.
 * @returns {'firetv' | 'roku' | null}
 */
// True when this session IS the native app (TV app routes, the mobile app
// webview, or a persisted TV appType) — never prompt to download it there.
const isInsideNativeApp = () => {
    try {
        if (localStorage.getItem("appType")) return true;
    } catch { /* storage blocked — fall through to the other checks */ }
    if ((navigator.userAgent || "").includes("GameCityApp")) return true;
    return /\/(roku|firetv|samsung|sky|app)(\/|$)/i.test(window.location.pathname);
};

export const resolveTvAppPlatform = () => {
    // Testing override: ?platform=firetv|roku forces that platform's prompt,
    // bypassing UA detection and the inside-the-app guard. (?platform=web is
    // App.js's existing "clear stored appType" escape hatch and is ignored here.)
    const forced = new URLSearchParams(window.location.search).get("platform");
    if (forced === "firetv" || forced === "roku") return forced;

    if (isInsideNativeApp()) return null;
    const tv = getCachedReport()?.tvHeuristics;
    if (tv) {
        if (tv.isAmazonSilk || tv.isFireTV) return "firetv";
        if (tv.isRoku) return "roku";
        return null;
    }
    const ua = navigator.userAgent || "";
    if (/Silk\/\d+/i.test(ua) || /\bAFT[0-9A-Z]+\b/i.test(ua)) return "firetv";
    if (/Roku|DVP-|RokuOS/i.test(ua)) return "roku";
    return null;
};
